import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ZoomModal from "./heatmap/zoomModal";
import { overallParticipationDivision } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import generateChartImage from "../admin/components/Charts/generateChartImage";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const OverallParticipationChart = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "Overall Participation (Division)" }) => {
  const [rowData, setRowData] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAttempt, setIsAttempt] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState("")
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  const [chartSeries, setChartSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const chartRef = useRef(null);

  const modalId = "overallZoomModal";

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "overall_participation_division",
        type: "bar",
        stacked: true,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "35%",
        },
      },
      colors: ["#dc3545", "#ffc107", "#28a745"],
      xaxis: { categories },
      yaxis: {
        min: 0,
        max: 60,
        tickAmount: 12,
        labels: { formatter: (value) => value },
      },
      dataLabels: { enabled: true },
      legend: {
        position: "top",
        onItemClick: { toggleDataSeries: false },
      },
    }),
    [categories]
  );

  const getAllParticipationDivisionData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await overallParticipationDivision(selectedCompanyId, selectedCampaignId,
        type, graphName);
      if (response?.data?.responseCode === 200) {
        const { remark = "", remarkId = null, isAttempt = 0, departmentStats = [] } = response.data;
        setRemarkText(response.data.remark || "");
        setRemarkId(response.data.remarkId || "");

        const departmentNames = departmentStats.map((d) => d.departmentName);
        const Not_Started = departmentStats.map((d) => d.notAttemptedEmployee || 0);
        const In_Progress = departmentStats.map((d) => d.inProgressEmployee || 0);
        const Completed = departmentStats.map((d) => d.attemptedEmployee || 0);

        setCategories(departmentNames);
        setChartSeries([
          { name: "Not Started", data: Not_Started },
          { name: "In Progress", data: In_Progress },
          { name: "Completed", data: Completed },
        ]);
        setRowData(response?.data);
        setDepartmentStats(departmentStats);
      } else {
        setRowData([]);
        if (response?.data?.responseCode !== 404) {
          toastEmitter("error", response?.data?.message);
        }
      }
    } catch {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedCampaignId,
    type, graphName]);

  useEffect(() => {
    if (selectedCompanyId) getAllParticipationDivisionData();
  }, [getAllParticipationDivisionData, selectedCompanyId]);

  useEffect(() => {
    if (chartSeries.some((series) => series.data.some((val) => val > 0))) {
      setTimeout(() => {
        generateChartImage("overall_participation_division", "overall_participation_division", rowData?.remark);
      }, 100);
    }
  }, [chartSeries, rowData]);

  const exportAsExcel = () => {
    if (!departmentStats.length) return;
    const titleRow = [title];
    const headers = ["Department Name", "Total Employees", "Not Attempted", "Attempted", "In Progress"];
    const rows = departmentStats.map((d) => [
      d.departmentName,
      d.totalEmployee,
      d.notAttemptedEmployee,
      d.attemptedEmployee,
      d.inProgressEmployee,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([titleRow, [], headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Department Summary");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${title}.xlsx`);
  };

  //  Export PDF
   const exportAsPDF = async () => {
  const chartBox = document.getElementById(`chart-box-${title}`);
  if (!chartBox) return;
  chartBox.scrollTo(0, 0);
  await new Promise(r => setTimeout(r, 200));
 
  const chartElement = chartBox.querySelector(".apexcharts-canvas");
  if (!chartElement) return;
 
  const canvas = await html2canvas(chartElement, {
    scale: 2,
    backgroundColor: "#fff",
    useCORS: true,
  });
 
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("l", "mm", "a4");
 
  const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
  const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF
 
  const canvasWidth = canvas.width; // Width of the canvas image
  const canvasHeight = canvas.height; // Height of the canvas image
 
  // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
  const scaleFactor = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%
 
  const imgWidth = canvasWidth * scaleFactor; // Scaled width
  const imgHeight = canvasHeight * scaleFactor; // Scaled height
 
  // Calculate the offset to center the image
  const xOffset = (pdfWidth - imgWidth) / 2;
  const yOffset = (pdfHeight - imgHeight) / 2;
 
  // Add the image to the PDF, centered and scaled down
  pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
 
  // Save the PDF
  pdf.save(`${title || "chart"}.pdf`);
};

const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <>
      <div className="card h-100 shadow-sm">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center overall-text  mb-2 p-2">
          <h5 className="m-0">{title}</h5>
          <div className="d-flex align-items-center">
            <button
              className="border-0 bg-transparent me-2"
              data-bs-toggle="modal"
              data-bs-target={`#${modalId}`}
            >
              <img src={ZOOM_ICON} alt="zoom" className="" />
            </button>
            <div className="dropdown">
              <button className="icon-btn" type="button" data-bs-toggle="dropdown">
                <img src={MENU_ICON} alt="menu" className="" />              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button className="dropdown-item" onClick={exportAsExcel}>
                    Download Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={exportAsPDF}>
                    Export PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={exportAsImage}>
                    Download Image
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div id={`chart-box-${title}`} className="overallParticipation">
          {loading ? (
            <div className="loader-container"><div className="loader"></div></div>
          ) : (
            <div
              className="chart9"
              data-heading={title}
              data-remark={remark}
            >
              <Chart
                ref={chartRef}
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={450}
                width="100%"
              />
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end my-2">
          <button
            title="Add Comment"
            onClick={() => {
              setShowRemarkModal(true);
              setTimeout(() => {
                if (modalRef.current) modalRef.current.click();
              }, 0);
            }}
            className=" border-0 bg-transparent position-absolute"
            style={{
              bottom: "10px",
              right: "10px",
            }}
          >
            <img src={CHAT_ICON} alt="chat" className="" />          </button>
        </div>

        {/* Remark Modal */}
        {showRemarkModal && (
          <Modal modalClick={modalRef} title="Remark" buttonLable="">
            <Remark
              selectedCompanyId={selectedCompanyId}
              selectedCampaignId={selectedCampaignId}
              type={type}
              graphName={graphName}
              initialRemark={remarkText}
              remarkId={remarkId}
              isAttempt={0}
              setRemarkprops={setRemarkText}
              setRemarkIdProps={setRemarkId}
              onCloseModal={() => setShowRemarkModal(false)}
            />
          </Modal>
        )}
        {/* Zoom Modal */}
        <ZoomModal
          id={modalId}
          title={title}
          chartOptions={chartOptions}
          seriesData={chartSeries}
          height={400}
          type="bar"
          remarkText={remarkText}
          setRemarkText={setRemarkText}
          showRemarkModal={showRemarkModal}
          setShowRemarkModal={setShowRemarkModal}
          modalRef={modalRef}
          exportSummaryToExcel={exportAsExcel}
          exportAsPDF={exportAsPDF}
          exportAsImage={exportAsImage}
        />
      </div>
    </>
  );
};

export default OverallParticipationChart;
